import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend } from 'recharts';
import { useSEO, generateWebPageSchema } from '../../utils/seo';

const DataInsightsPage = () => {
  const [activeSection, setActiveSection] = useState('overview');

  // SEO Configuration
  useSEO({
    title: 'Cervical Cancer Data & Statistics | Insights on Screening & Prevention',
    description: 'Educational insights about cervical cancer trends, risk factors, age distribution, and screening impact. Data-driven information showing why early detection saves lives with 93% survival rate.',
    keywords: 'cervical cancer statistics, cancer data India, screening statistics, survival rates, risk factor data, cancer trends',
    canonical: '/data-insights',
    schema: generateWebPageSchema(
      'Cervical Cancer Data Insights',
      'Educational insights about cervical cancer trends, risk factors, and the importance of early screening',
      '/data-insights'
    ),
  });

  const ageDistributionData = [
    { age: 'Under 20', cases: 2, percentage: 1 },
    { age: '20-29', cases: 15, percentage: 8 },
    { age: '30-39', cases: 35, percentage: 19 },
    { age: '40-49', cases: 48, percentage: 26 },
    { age: '50-59', cases: 52, percentage: 28 },
    { age: '60+', cases: 33, percentage: 18 },
  ];

  const riskFactorsData = [
    { factor: 'No Screening', impact: 85 },
    { factor: 'Smoking', impact: 72 },
    { factor: 'Multiple Partners', impact: 65 },
    { factor: 'Early Sexual Activity', impact: 58 },
    { factor: 'Long-term Contraceptives', impact: 45 },
    { factor: 'Multiple Pregnancies', impact: 38 },
  ];

  const screeningImpactData = [
    { name: 'Regular Screening', value: 93 },
    { name: 'Irregular Screening', value: 45 },
    { name: 'No Screening', value: 12 },
  ];

  const COLORS = ['#14B8A6', '#FF6B6B', '#A78BFA'];

  const symptomsData = [
    { symptom: 'Abnormal Bleeding', percentage: 68 },
    { symptom: 'Pelvic Pain', percentage: 52 },
    { symptom: 'Unusual Discharge', percentage: 45 },
    { symptom: 'Pain During Sex', percentage: 38 },
    { symptom: 'Back Pain', percentage: 32 },
    { symptom: 'Fatigue', percentage: 28 },
  ];

  const earlyDetectionData = [
    { stage: 'Stage I', survival: 93, cases: 45 },
    { stage: 'Stage II', survival: 73, cases: 30 },
    { stage: 'Stage III', survival: 45, cases: 18 },
    { stage: 'Stage IV', survival: 17, cases: 7 },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-teal-50/30">
      <Navbar />

      <div className="pt-32 pb-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold font-display text-gray-800 mb-4">
              Data <span className="text-primary">Insights</span>
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Educational insights about cervical cancer trends, risk factors, and the importance of early screening
            </p>
          </div>

          <div className="grid lg:grid-cols-4 gap-8 mb-12">
            {[
              { icon: 'ri-bar-chart-line', label: 'Overview', value: 'overview' },
              { icon: 'ri-user-line', label: 'Age Trends', value: 'age' },
              { icon: 'ri-alert-line', label: 'Risk Factors', value: 'risk' },
              { icon: 'ri-heart-pulse-line', label: 'Symptoms', value: 'symptoms' },
            ].map((item) => (
              <button
                key={item.value}
                onClick={() => setActiveSection(item.value)}
                className={`p-6 rounded-xl transition-all duration-300 cursor-pointer ${
                  activeSection === item.value
                    ? 'bg-gradient-to-br from-primary to-teal-500 text-white shadow-xl'
                    : 'bg-white text-gray-700 hover:shadow-lg'
                }`}
              >
                <i className={`${item.icon} text-3xl mb-2`}></i>
                <div className="font-semibold">{item.label}</div>
              </button>
            ))}
          </div>

          {activeSection === 'overview' && (
            <div className="space-y-8 animate-fade-in">
              <div className="grid md:grid-cols-3 gap-6">
                {[
                  { icon: 'ri-shield-check-line', label: 'Prevention Rate', value: '90%+', color: 'green' },
                  { icon: 'ri-heart-pulse-line', label: 'Early Detection Survival', value: '93%', color: 'teal' },
                  { icon: 'ri-calendar-check-line', label: 'Screening Importance', value: 'Critical', color: 'blue' },
                ].map((stat, index) => (
                  <div key={index} className="bg-white rounded-2xl p-8 shadow-lg text-center">
                    <div className={`w-16 h-16 flex items-center justify-center bg-${stat.color}-100 rounded-full mx-auto mb-4`}>
                      <i className={`${stat.icon} text-3xl text-${stat.color}-600`}></i>
                    </div>
                    <div className={`text-4xl font-bold text-${stat.color}-600 mb-2`}>{stat.value}</div>
                    <div className="text-gray-600">{stat.label}</div>
                  </div>
                ))}
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-gray-800 mb-6">Screening Impact on Early Detection</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={screeningImpactData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value }) => `${name}: ${value}%`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {screeningImpactData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <p className="text-gray-600 text-center mt-6">
                  Regular screening dramatically increases early detection rates, leading to better treatment outcomes
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-gray-800 mb-6">Survival Rates by Stage at Detection</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={earlyDetectionData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="stage" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="survival" fill="#14B8A6" name="5-Year Survival Rate (%)" />
                  </BarChart>
                </ResponsiveContainer>
                <p className="text-gray-600 text-center mt-6">
                  <strong>Key Insight:</strong> Early detection (Stage I) has a 93% survival rate, emphasizing the critical importance of regular screening
                </p>
              </div>
            </div>
          )}

          {activeSection === 'age' && (
            <div className="space-y-8 animate-fade-in">
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-gray-800 mb-6">Age Distribution of Cervical Cancer Cases</h3>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={ageDistributionData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="age" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="percentage" fill="#14B8A6" name="Percentage of Cases (%)" />
                  </BarChart>
                </ResponsiveContainer>
                <div className="mt-8 space-y-4">
                  <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-lg">
                    <h4 className="font-bold text-gray-800 mb-2">Peak Age Range: 40-59 Years</h4>
                    <p className="text-gray-700">
                      Over 54% of cervical cancer cases occur in women aged 40-59, highlighting the importance of consistent screening during these decades.
                    </p>
                  </div>
                  <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 rounded-lg">
                    <h4 className="font-bold text-gray-800 mb-2">Younger Women at Risk Too</h4>
                    <p className="text-gray-700">
                      While less common, 27% of cases occur in women under 40, emphasizing that screening should begin at age 21 regardless of perceived risk.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeSection === 'risk' && (
            <div className="space-y-8 animate-fade-in">
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-gray-800 mb-6">Major Risk Factors Impact</h3>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={riskFactorsData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" domain={[0, 100]} />
                    <YAxis dataKey="factor" type="category" width={150} />
                    <Tooltip />
                    <Bar dataKey="impact" fill="#FF6B6B" name="Risk Impact Score" />
                  </BarChart>
                </ResponsiveContainer>
                <div className="mt-8 grid md:grid-cols-2 gap-6">
                  <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-lg">
                    <h4 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
                      <i className="ri-alert-line text-red-500"></i>
                      Highest Risk: No Screening
                    </h4>
                    <p className="text-gray-700">
                      Lack of regular screening is the single biggest risk factor, with an impact score of 85. Regular screening can detect precancerous changes early.
                    </p>
                  </div>
                  <div className="bg-orange-50 border-l-4 border-orange-500 p-6 rounded-lg">
                    <h4 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
                      <i className="ri-smoke-line text-orange-500"></i>
                      Smoking Impact
                    </h4>
                    <p className="text-gray-700">
                      Smoking has a risk impact score of 72, making it the second-highest modifiable risk factor. Quitting smoking significantly reduces risk.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeSection === 'symptoms' && (
            <div className="space-y-8 animate-fade-in">
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-gray-800 mb-6">Common Symptoms Reported</h3>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={symptomsData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="symptom" angle={-45} textAnchor="end" height={120} />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="percentage" fill="#A78BFA" name="Percentage of Patients (%)" />
                  </BarChart>
                </ResponsiveContainer>
                <div className="mt-8 bg-yellow-50 border-l-4 border-yellow-500 p-6 rounded-lg">
                  <div className="flex items-start gap-4">
                    <i className="ri-information-line text-3xl text-yellow-600 flex-shrink-0"></i>
                    <div>
                      <h4 className="font-bold text-gray-800 mb-2">Important Note About Symptoms</h4>
                      <p className="text-gray-700 mb-3">
                        <strong>Early-stage cervical cancer often has NO symptoms.</strong> These symptoms typically appear only in advanced stages, which is why regular screening is essential even when you feel healthy.
                      </p>
                      <p className="text-gray-700">
                        Abnormal bleeding is the most commonly reported symptom (68%), but waiting for symptoms to appear means missing the opportunity for early detection when treatment is most effective.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="mt-16 bg-gradient-to-br from-primary to-teal-600 rounded-3xl p-12 text-white text-center">
            <h2 className="text-3xl font-bold mb-4">The Bottom Line</h2>
            <p className="text-xl mb-8 leading-relaxed max-w-3xl mx-auto">
              Data consistently shows that <strong>regular screening saves lives</strong>. Cervical cancer is highly preventable and treatable when detected early. Don't wait for symptoms—take action today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/consent"
                className="inline-flex items-center gap-3 px-8 py-4 bg-white text-primary rounded-full text-lg font-semibold hover:shadow-2xl hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                Start Health Check
                <i className="ri-arrow-right-line text-xl"></i>
              </a>
              <a
                href="/prevention"
                className="inline-flex items-center gap-3 px-8 py-4 border-2 border-white text-white rounded-full text-lg font-semibold hover:bg-white hover:text-primary transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                Prevention Guide
                <i className="ri-book-open-line text-xl"></i>
              </a>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default DataInsightsPage;
